import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useAuth } from '../../lib/api/auth';
import { useFamilyData } from '../../lib/context/FamilyDataContext';
import { aiApi } from '../../lib/api/endpoints';

interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  time: string;
  matchedRule?: { name: string; points: number; childName: string };
}

interface LocationState {
  autoSend?: boolean;
  message?: string;
  childId?: string;
  childName?: string;
}

const welcomeMessage: Message = {
  id: '0', role: 'ai', time: '09:00',
  content: '你好！我是习惯星球AI助手 🌟\n\n你可以用自然语言告诉我孩子的表现，我会自动匹配奖惩机制并记录积分。\n\n例如说：\n• "小明今天7点半前起床了"\n• "张小红帮忙洗碗做家务"\n• "小明作业拖延到10点还没完成"'
};

// localStorage key prefix
const CHAT_STORAGE_PREFIX = 'habit_orbit_ai_chat_';
const MAX_STORED_MESSAGES = 20; // 保存最近20条消息

// 从localStorage加载消息（按家庭ID分离）
function loadMessages(familyId?: string): Message[] {
  if (!familyId) return [welcomeMessage];
  try {
    const stored = localStorage.getItem(`${CHAT_STORAGE_PREFIX}${familyId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load chat history:', e);
  }
  return [welcomeMessage];
}

// 保存消息到localStorage（按家庭ID分离）
function saveMessages(messages: Message[], familyId?: string) {
  if (!familyId) return;
  try {
    const toSave = messages.slice(-MAX_STORED_MESSAGES);
    localStorage.setItem(`${CHAT_STORAGE_PREFIX}${familyId}`, JSON.stringify(toSave));
  } catch (e) {
    console.error('Failed to save chat history:', e);
  }
}

// 清理AI回复中的JSON包装，提取实际内容
function cleanAiReply(content: string): string {
  if (!content) return '';

  let text = content.trim();

  // 1. 去掉 ```json ... ``` 包装
  const codeBlockMatch = text.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?\s*```$/);
  if (codeBlockMatch) {
    text = codeBlockMatch[1].trim();
  }

  // 2. 处理以 {"reply": 开头的JSON格式
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text);
      if (parsed.reply) return parsed.reply;
      if (parsed.content) return parsed.content;
      if (parsed.message) return parsed.message;
    } catch {
      // JSON解析失败，尝试正则提取
      const replyMatch = text.match(/"reply"\s*:\s*"([\s\S]*)/);
      if (replyMatch) {
        let extracted = replyMatch[1];
        // 去掉末尾的 "} 或 }"
        extracted = extracted.replace(/"?\s*\}?\s*$/, '');
        return extracted.replace(/\\n/g, '\n').replace(/\\"/g, '"');
      }
    }
  }

  // 3. 处理以 "reply": 开头但不是完整JSON的情况
  if (text.startsWith('"reply"')) {
    let extracted = text.replace(/^"reply"\s*:\s*"?/, '');
    extracted = extracted.replace(/"?\s*\}?\s*$/, '');
    return extracted.replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }

  // 4. 处理字面 \n 转义字符
  text = text.replace(/\\n/g, '\n');

  return text;
}

export default function AIPage() {
  const { currentFamily } = useAuth();
  const { children, rewardRules, punishRules, ensureRulesLoaded, forceRefresh } = useFamilyData();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  const [messages, setMessages] = useState<Message[]>(() => loadMessages(currentFamily?.id));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiLimitExceeded, setAiLimitExceeded] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>(['加载中...']);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 切换家庭时加载对应聊天记录
  useEffect(() => {
    setMessages(loadMessages(currentFamily?.id));
    setAiLimitExceeded(null);
  }, [currentFamily?.id]);

  // 加载规则后生成快捷建议 + 检查AI使用限制
  useEffect(() => {
    ensureRulesLoaded();
    if (currentFamily?.id) {
      aiApi.getUsage(currentFamily.id).then(res => {
        if (res.code === 0 && res.data?.is_exceeded) {
          setAiLimitExceeded(res.data.ai_limit > 0
            ? `本周免费AI聊天次数已用完（${res.data.ai_used}/${res.data.ai_limit}次/周），升级VIP可享受无限AI对话。`
            : '已达免费次数上限，升级VIP可享受无限AI对话。');
        }
      }).catch(() => {});
    }
  }, [currentFamily?.id]);

  useEffect(() => {
    if (children.length === 0) {
      setSuggestions(['今天孩子表现很棒', '帮忙做家务了', '按时完成作业']);
      return;
    }
    if (rewardRules.length === 0 && punishRules.length === 0) return; // 等待规则加载

    const childNames = children.map(c => c.nickname || c.name).filter(Boolean);
    const ruleNames = [
      ...rewardRules.map(r => r.name),
      ...punishRules.map(r => r.name),
    ];

    if (childNames.length === 0 || ruleNames.length === 0) {
      setSuggestions(['今天孩子表现很棒', '帮忙做家务了', '按时完成作业']);
      return;
    }

    const result: string[] = [];
    const used = new Set<string>();
    let attempts = 0;

    while (result.length < 5 && attempts < 30) {
      attempts++;
      const name = childNames[Math.floor(Math.random() * childNames.length)];
      const rule = ruleNames[Math.floor(Math.random() * ruleNames.length)];
      const key = `${name}-${rule}`;
      if (used.has(key)) continue;
      used.add(key);
      result.push(`${name}${rule}`);
    }

    setSuggestions(result.length > 0 ? result : ['今天孩子表现很棒', '帮忙做家务了', '按时完成作业']);
  }, [children, rewardRules, punishRules]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 消息变化时保存
  useEffect(() => {
    saveMessages(messages, currentFamily?.id);
  }, [messages, currentFamily?.id]);

  // 从习惯分析页跳转过来时自动发送分析请求（仅一次，刷新后不重复）
  useEffect(() => {
    if (state?.autoSend && state?.message && currentFamily?.id) {
      // 用 sessionStorage 记录已发送的消息摘要，防止刷新重复
      const sentKey = `ai_auto_sent_${currentFamily.id}_${state.message.slice(0, 50).replace(/\s/g, '')}`;
      if (sessionStorage.getItem(sentKey)) return;
      sessionStorage.setItem(sentKey, '1');

      // 清除 location state
      window.history.replaceState({}, '', window.location.pathname);

      setTimeout(() => {
        handleSendWithMessage(state.message!);
      }, 100);
    }
  }, [state?.autoSend, currentFamily?.id]);

  const handleSend = async () => {
    if (!input.trim() || !currentFamily?.id) return;

    const now = new Date();
    const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: input, time };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await aiApi.chat({
        family_id: currentFamily.id,
        message: input
      });

      if (response.code === 0 && response.data) {
        // 检查是否超出限制
        if (response.data.action === 'limit_exceeded') {
          setAiLimitExceeded(response.data.reply);
        }

        const aiMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          time,
          content: cleanAiReply(response.data.reply),
          matchedRule: response.data.parsed_records?.[0]
        };
        setMessages(prev => [...prev, aiMsg]);

        // 如果AI操作成功（创建规则、兑换等），强制刷新家庭数据
        if (response.data.processed || response.data.reply.includes('已创建') || response.data.reply.includes('兑换成功')) {
          forceRefresh();
          // 触发TimerTask刷新（通过自定义事件）
          window.dispatchEvent(new CustomEvent('timer-tasks-changed'));
        }
      }
    } catch (error: any) {
      console.error('Failed to send AI message:', error);
      // 尝试提取API返回的具体错误信息
      let errorMsg = '抱歉，出现了一些问题，请稍后再试。';
      if (error?.message?.includes('HTTP 403')) {
        errorMsg = '无权限访问该家庭，请确认您是该家庭的成员。';
      } else if (error?.message?.includes('HTTP 500')) {
        errorMsg = '服务器暂时出错，请稍后再试。';
      } else if (error?.message?.includes('网络') || error?.message?.includes('fetch')) {
        errorMsg = '网络连接失败，请检查网络后重试。';
      }
      const msg: Message = { id: (Date.now() + 1).toString(), role: 'ai', time, content: errorMsg };
      setMessages(prev => [...prev, msg]);
    } finally {
      setLoading(false);
    }
  };

  // 发送指定消息（用于从习惯分析页自动发送）
  const handleSendWithMessage = async (message: string) => {
    if (!message.trim() || !currentFamily?.id) return;

    const now = new Date();
    const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: message, time };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await aiApi.chat({
        family_id: currentFamily.id,
        message: message
      });

      if (response.code === 0 && response.data) {
        if (response.data.action === 'limit_exceeded') {
          setAiLimitExceeded(response.data.reply);
        }

        const aiMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          time,
          content: cleanAiReply(response.data.reply),
          matchedRule: response.data.parsed_records?.[0]
        };
        setMessages(prev => [...prev, aiMsg]);

        if (response.data.processed || response.data.reply.includes('已创建') || response.data.reply.includes('兑换成功')) {
          forceRefresh();
          window.dispatchEvent(new CustomEvent('timer-tasks-changed'));
        }
      }
    } catch (error) {
      console.error('Failed to send AI message:', error);
      let errorMsg = '抱歉，出现了一些问题，请稍后再试。';
      if (error?.message?.includes('HTTP 403')) {
        errorMsg = '无权限访问该家庭，请确认您是该家庭的成员。';
      } else if (error?.message?.includes('HTTP 500')) {
        errorMsg = '服务器暂时出错，请稍后再试。';
      } else if (error?.message?.includes('网络') || error?.message?.includes('fetch')) {
        errorMsg = '网络连接失败，请检查网络后重试。';
      }
      const msg: Message = { id: (Date.now() + 1).toString(), role: 'ai', time, content: errorMsg };
      setMessages(prev => [...prev, msg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    if (confirm('确定要清空聊天记录吗？')) {
      setMessages([welcomeMessage]);
    }
  };

  return (
    <DashboardLayout>
      {/* 全屏flex列布局，头部底部固定，中间滚动 */}
      <div className="flex flex-col w-full overflow-x-hidden" style={{ height: 'calc(100vh - 56px)' }}>

        {/* Header - 固定顶部 */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-blue-100 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 flex-shrink-0">
                <i className="ri-robot-2-fill text-white text-xl"></i>
              </div>
              <div>
                <h1 className="font-black text-blue-900 text-base">AI习惯助手</h1>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                  <p className="text-xs text-gray-400">智能识别 · 自动记录</p>
                </div>
              </div>
            </div>
            <button
              onClick={handleClearChat}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
              title="清空聊天记录"
            >
              <i className="ri-delete-bin-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Messages - 可滚动中间区域 */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4 bg-[#F0F7FF] min-h-0">
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-3`}>
              {msg.role === 'ai' && (
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 flex-shrink-0 mt-0.5">
                  <i className="ri-robot-2-fill text-white text-sm"></i>
                </div>
              )}
              <div className={`max-w-[75%] flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-br from-blue-600 to-blue-900 text-white rounded-tr-sm whitespace-pre-line'
                    : 'bg-white text-gray-700 rounded-tl-sm border border-blue-50'
                }`}>
                  {msg.role === 'ai' ? (
                    <div className="prose prose-sm prose-blue max-w-full break-words prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-li:my-0.5 prose-strong:text-blue-800 overflow-hidden">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {cleanAiReply(msg.content)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                  {msg.matchedRule && (
                    <div className={`mt-3 p-2.5 rounded-xl border ${msg.matchedRule.points > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                      <div className="flex items-center gap-2">
                        <i className={`${msg.matchedRule.points > 0 ? 'ri-trophy-fill text-emerald-500' : 'ri-alert-fill text-rose-500'}`}></i>
                        <span className={`text-xs font-bold ${msg.matchedRule.points > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {msg.matchedRule.childName} · {msg.matchedRule.name} · {msg.matchedRule.points > 0 ? '+' : ''}{msg.matchedRule.points}分
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-gray-400">{msg.time}</span>
              </div>
              {msg.role === 'user' && (
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-100 flex-shrink-0 mt-0.5">
                  <i className="ri-parent-fill text-blue-500 text-sm"></i>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex justify-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-blue-900 flex-shrink-0">
                <i className="ri-robot-2-fill text-white text-sm"></i>
              </div>
              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 border border-blue-50">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef}></div>
        </div>

        {/* Bottom area - 固定底部，建议词 + 输入框 */}
        <div className="flex-shrink-0 bg-white border-t border-blue-100">
          {/* AI次数限制提示 */}
          {aiLimitExceeded && (
            <div className="mx-4 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
                <i className="ri-vip-crown-fill text-amber-500 text-xs"></i>
              </div>
              <div className="flex-1">
                <p className="text-xs text-amber-700 font-medium">{aiLimitExceeded}</p>
              </div>
              <button onClick={() => navigate('/vip')} className="flex-shrink-0 bg-amber-500 text-white text-[11px] font-medium px-3 py-1.5 rounded-lg hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap">
                开通VIP
              </button>
            </div>
          )}
          {/* Suggestions */}
          {!aiLimitExceeded && (
            <div className="px-4 pt-2 pb-1">
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => setInput(s)}
                    className="flex-shrink-0 px-3 py-1.5 bg-blue-50 text-blue-600 text-xs rounded-full cursor-pointer hover:bg-blue-100 transition-colors whitespace-nowrap border border-blue-100">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Input */}
          <div className="px-4 pb-4 pt-1">
            <div className={`flex items-end gap-3 bg-blue-50 rounded-2xl px-4 py-3 border border-blue-200 transition-colors ${aiLimitExceeded ? 'opacity-60' : 'focus-within:border-blue-400'}`}>
              <textarea
                value={input}
                onChange={e => !aiLimitExceeded && setInput(e.target.value)}
                onKeyDown={!aiLimitExceeded ? handleKeyDown : undefined}
                placeholder={aiLimitExceeded ? '已达免费次数上限，升级VIP可无限使用' : '描述孩子的表现，AI自动识别并记录积分...'}
                rows={2}
                className="flex-1 bg-transparent text-sm text-gray-700 resize-none focus:outline-none placeholder:text-gray-400"
                disabled={!!aiLimitExceeded}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading || !!aiLimitExceeded}
                className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all cursor-pointer flex-shrink-0 ${
                  input.trim() && !loading && !aiLimitExceeded ? 'bg-gradient-to-br from-blue-500 to-blue-800 hover:opacity-90' : 'bg-gray-200'
                }`}>
                <i className={`ri-send-plane-fill text-sm ${input.trim() && !loading && !aiLimitExceeded ? 'text-white' : 'text-gray-400'}`}></i>
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">Enter发送 · Shift+Enter换行 · AI自动匹配奖惩规则</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
